<template>
  <CloseHeader @close="onCloseHeader"></CloseHeader>
  <div class="app-container one-page">
    <div class="join-con">
      <div class="join-item nation-select">
        <template v-if="countryStore.countries.length > 0">
          <p>{{ t('label_select_country') }}</p>

          <button type="button" class="nation-btn l-btn" @click="openCountryPopup">
            {{ signupForm.countryCode?.code ? signupForm.countryCode?.name : t('alert_select_country') }}
            <IconArrow class="icon" />
          </button>
        </template>
      </div>

      <div class="join-item code">
        <p>{{ t('referral_code') }}</p>
        <input
          v-model="signupForm.referralCode"
          v-cursor-end
          class="app-t-input"
          type="text"
          enterkeyhint="done"
          :placeholder="t('referral_code_placeholder')"
          @keydown.enter.prevent="(e) => (e.target as HTMLInputElement).blur()"
        />
      </div>

      <div class="join-item agree">
        <p>
          <i18n-t keypath="terms_of_service_agreement_required">
            <template #language>
              <span class="primary">{{ t('terms_agree') }}</span>
            </template>
          </i18n-t>
        </p>
        <div class="all-wrap ck-item">
          <input id="all-agree" v-model="isAllChecked" type="checkbox" class="app-ck" />
          <div class="app-ck-wrap all-box">
            <label for="all-agree" class="app-ck-icon">
              <IconCheck></IconCheck>
            </label>
            <span>{{ t('agree_to_all') }}</span>
          </div>
        </div>

        <ul class="agree-wrap">
          <li>
            <div class="app-ck-wrap ck-item">
              <input id="agree01" v-model="agreementForm.isAgreedPersonal" type="checkbox" class="app-ck" />
              <label for="agree01" class="app-ck-icon">
                <IconCheck></IconCheck>
              </label>
              <span>{{ t('policy_service') }} ({{ t('required') }})</span>
            </div>
            <IconArrow class="icon" @click="openPolicyPopup('policy-service')" />
          </li>

          <li>
            <div class="app-ck-wrap ck-item">
              <input id="agree02" v-model="agreementForm.isAgreedPayment" type="checkbox" class="app-ck" />
              <label for="agree02" class="app-ck-icon">
                <IconCheck></IconCheck>
              </label>
              <span>{{ t('policy_privacy') }} ({{ t('required') }})</span>
            </div>
            <IconArrow class="icon" @click="openPolicyPopup('policy-privacy')" />
          </li>

          <li>
            <div class="app-ck-wrap ck-item">
              <input id="agree03" v-model="agreementForm.isAgreedUsage" type="checkbox" class="app-ck" />
              <label for="agree03" class="app-ck-icon">
                <IconCheck></IconCheck>
              </label>
              <span>{{ t('policy_payment') }} ({{ t('required') }})</span>
            </div>
            <IconArrow class="icon" @click="openPolicyPopup('policy-refund')" />
          </li>

          <li>
            <div class="app-ck-wrap ck-item">
              <input id="agree04" v-model="agreementForm.isAgreedStorage" type="checkbox" class="app-ck" />
              <label for="agree04" class="app-ck-icon">
                <IconCheck></IconCheck>
              </label>
              <span>{{ t('policy_cookie') }} ({{ t('optional') }})</span>
            </div>
            <IconArrow class="icon" @click="openPolicyPopup('policy-privacy')" />
          </li>

          <li>
            <div class="app-ck-wrap ck-item">
              <input id="agree05" v-model="agreementForm.isAgreedMarketing" type="checkbox" class="app-ck" />
              <label for="agree05" class="app-ck-icon">
                <IconCheck></IconCheck>
              </label>
              <span>{{ t('policy_marketing') }} ({{ t('optional') }})</span>
            </div>
            <!-- <IconArrow class="icon" @click="openPolicyPopup('policy-privacy')" /> -->
          </li>
        </ul>
      </div>
    </div>

    <div class="app-btn-wrap">
      <button type="button" class="primary-btn l-btn" :disabled="!isRequiredChecked" @click="moveNextStep">
        <span>{{ t('button_start') }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { fetchUserCountry, signupOAuth } from '@/api/apiService';
import CloseHeader from '@/components/domains/mobile/common/CloseHeader.vue';
import BottomSheet from '@/components/domains/mobile/common/PopupBottomSheet.vue';
import Modal from '@/components/domains/mobile/common/PopupModal.vue';
import PopupPolicyInfo from '@/components/domains/mobile/popup/PopupPolicyInfo.vue';
import PopupPolicyPrivacy from '@/components/domains/mobile/popup/PopupPolicyPrivacy.vue';
import PopupPolicyRefund from '@/components/domains/mobile/popup/PopupPolicyRefund.vue';
import PopupPolicyService from '@/components/domains/mobile/popup/PopupPolicyService.vue';
import PopupSelectCountry from '@/components/domains/mobile/popup/PopupSelectCountry.vue';
import IconArrow from '@/components/jh-app/icons/IconArrow.vue';
import IconCheck from '@/components/jh-app/icons/IconCheck.vue';
import { useLogin } from '@/composables/shared/useLogin';
import { useCountryStore } from '@/stores/country';
import { usePopupStore } from '@/stores/popup';
import { useUserStore } from '@/stores/user';
import { AppAgreementForm, AppSignupForm, Country, PolicyNameType, SignupInfo } from '@/types/components';
import { SOCIAL_PROVIDER_MAPPER } from '@/utils/constants/common';
import { stringToBase64UrlSafe } from '@/utils/encryptor';
import { secureStorageSet, secureStorageGet } from '@/utils/native/nativeBridge';

const { goToLoginPage } = useLogin();

const { t } = useI18n();
const router = useRouter();

const userStore = useUserStore();
const countryStore = useCountryStore();
const popupStore = usePopupStore();

const signupForm = ref<AppSignupForm>({
  oAuth: '',
  userId: '',
  accessToken: '',
  email: '',
  countryCode: null,
  referralCode: '',
});
const agreementForm = ref<AppAgreementForm>({
  isAgreedUsage: false,
  isAgreedPersonal: false,
  isAgreedPayment: false,
  isAgreedStorage: false,
  isAgreedMarketing: false,
});

const isLoading = ref<boolean>(false);
let signupInfo: SignupInfo = { oAuth: '', userId: '', accessToken: '', email: '' };

const isAllChecked = computed({
  get() {
    return Object.values(agreementForm.value).every((val) => val);
  },
  set(val: boolean) {
    Object.keys(agreementForm.value).forEach((key) => {
      agreementForm.value[key] = val;
    });
  },
});

const isRequiredChecked = computed<boolean>(() => {
  return ['isAgreedUsage', 'isAgreedPersonal', 'isAgreedPayment'].every((key) => agreementForm.value[key]);
});

const initSignupForm = async () => {
  try {
    const signupInfoRaw = await secureStorageGet({ key: 'signupInfo' });
    signupInfo = signupInfoRaw ? JSON.parse(signupInfoRaw) : {};
  } catch (error) {
    console.error('signupInfo 조회 실패', error);
  }

  const { oAuth = '', userId = '', accessToken = '', email = '', nickname = '' } = signupInfo;
  signupForm.value = {
    ...signupForm.value,
    oAuth: String(oAuth),
    userId: String(userId),
    accessToken: String(accessToken),
    email: String(email),
    nickname: String(nickname),
  };
};

const openCountryPopup = () => {
  popupStore.open(BottomSheet, {
    title: t('change_country'),
    contentSlot: h(PopupSelectCountry),
    payload: signupForm.value.countryCode,
    showCloseButton: true,
    confirmButton: {
      action: (country: Country) => {
        signupForm.value.countryCode = country;
      },
    },
  });
};

const moveNextStep = async () => {
  if (isLoading.value) {
    return;
  }

  await requestCreateUser();
};

/**
 * @desc 사용자의 현재 위치 정보를 조회합니다.
 * */
const requestUserCountry = async () => {
  try {
    const { data } = await fetchUserCountry();
    const countryIsoCode = data.countryIsoCode;

    if (countryIsoCode) {
      signupForm.value.countryCode = countryStore.getCountryByCode(countryIsoCode);
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * @desc 사용자 계정 생성을 요청합니다.
 * */
const requestCreateUser = async () => {
  isLoading.value = true;

  try {
    const { oAuth, userId, accessToken, email, countryCode, referralCode } = signupForm.value;
    const { isAgreedStorage, isAgreedMarketing } = agreementForm.value;

    const paramsSignup = {
      oAuth,
      userId,
      accessToken,
      email,
      countryCode: countryCode ? countryCode.code : null,
      referralCode,
      storeAgreement: isAgreedStorage,
      marketingAgreement: isAgreedMarketing,
      languageCode: userStore.pageLanguage,
    };

    if (oAuth === 'apple') {
      const nickname = signupForm.value.nickname?.trim();
      if (nickname) {
        paramsSignup['nickname'] = stringToBase64UrlSafe(nickname);
      }
    }

    const resSignup = await signupOAuth(paramsSignup, true);

    if (resSignup.success) {
      const { accessToken: accessTokenNew, refreshToken } = resSignup.data || {};

      await secureStorageSet({ key: 'authToken', value: accessTokenNew });

      const userInfo = await userStore.fetchUserInfo();

      await userStore.setLoginInfo({
        ...userInfo,
        token: accessTokenNew,
        loginType: 'SELF_SERVICE',
        partnerCode: null,
        refreshToken: refreshToken,
        ...(oAuth === 'apple' && { nickname: signupForm.value.nickname }),
      });

      const provider = signupInfo.oAuth;
      const registerType = SOCIAL_PROVIDER_MAPPER[provider];
      if (!registerType) {
        throw new Error(`Unknown OAuth provider: ${provider}`);
      }

      await secureStorageSet({
        key: 'authSession',
        value: JSON.stringify({
          refreshToken,
          provider: registerType,
        }),
      });

      await router.replace({ name: 'app-onboarding-step1' });
    } else if (resSignup.errorCode === 4040) {
      popupStore.openToast(t('error_referral_code'));
      isLoading.value = false;
      return;
    } else {
      throw new Error(`'resSignup.errorCode:' ${resSignup.errorCode}`);
    }
  } catch (error) {
    console.error('모바일 사용자 생성에 실패했습니다.', error);
    const popupId = popupStore.open(Modal, {
      title: t('retry_after_few_minutes'),
      subTitle: t('FORBIDDEN_REQUEST'),
      confirmButton: {
        text: t('confirm'),
        action: () => {
          popupStore.close(popupId);
        },
      },
    });
  }

  isLoading.value = false;
};

const openPolicyPopup = (name: PolicyNameType) => {
  const policyComponent = {
    'policy-service': PopupPolicyService,
    'policy-privacy': PopupPolicyPrivacy,
    'policy-refund': PopupPolicyRefund,
    'policy-info': PopupPolicyInfo,
  }[name];

  popupStore.open(BottomSheet, {
    contentSlot: h(policyComponent),
    showCloseButton: true,
  });
};

const onCloseHeader = async () => {
  const signupInfo = await secureStorageGet({ key: 'signupInfo' });
  const { oAuth } = JSON.parse(signupInfo || '{}');
  if (!oAuth) return;

  goToLoginPage();
};

onMounted(async () => {
  await countryStore.requestCountries();
  await requestUserCountry();
  await initSignupForm();
});
</script>

<style scoped lang="scss">
.join-con {
  padding-top: 1rem;
  .join-item {
    margin-bottom: 2rem;
    p {
      margin-bottom: $size-s;
      font-weight: 600;
    }
    .nation-btn {
      justify-content: space-between;
      border: 1px solid $color-gray-900;
      padding: 0 1rem 0;
      svg {
        transform: rotate(180deg);
      }
      .country-dropdown {
        outline: none;
        padding: 0 0 0 1rem;
      }
    }
    &.agree {
      .ck-item {
        span {
          font-size: $font-size-psmall;
        }
      }
      .all-wrap {
        .all-box {
          background: $gray10;
          padding: 0.875rem 1rem;
          border-radius: $size-s;
          span {
            font-weight: 600;
          }
        }
        .app-ck {
          &:checked {
            + .all-box {
              background: $primary-bg;
              span {
                color: $color-navy-100;
              }
            }
          }
        }
      }
      .agree-wrap {
        padding: 1rem 0.125rem;
        li {
          display: flex;
          justify-content: space-between;
          padding: 0.5rem 0;
          > svg {
            transform: rotate(180deg);
          }
        }
      }
    }
  }
}
.app-btn-wrap {
  button {
    width: 100%;
  }
}
</style>
